import { BaseClass } from '@components/BaseClass.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { isArray, zoomHandler } from '@components/Utility/index.js';
import { forkJoin, of, switchMap, tap } from 'rxjs';
/**
 * A base class for table specific business logic classes (ie. Contact extends RecordLogic) that are lazy loaded.
 * provides a standard set of functions like `userChange` that would be extended as needed by table specific versions.
 */
export class RecordLogic extends BaseClass {
    addBusinessLogicMethod(name, handler) {
        if (!this.runBusinessLogicMapping) {
            this.runBusinessLogicMapping = {};
        }
        this.runBusinessLogicMapping[name] = handler;
    }
    constructor() {
        super();
        this.pkName = '';
        this.tableName = '';
        this.formName = '';
        this.validateOnPreviewDelete = false;
        this.defaultSpecialEditColumns = [];
    }
    get CaptionManager() {
        return DIContainer.getInstance('CaptionManager');
    }
    get ConditionsManager() {
        return DIContainer.getInstance('ConditionsManager');
    }
    customerSelectModalGet() {
        return DIContainer.resolveLazy('CustomerSelectModal')
            .pipe(tap((modal) => {
            modal.createEmptyRecord();
        }));
    }
    dispatchSelectModalGet() {
        return DIContainer.resolveLazy('DispatchSelectModal')
            .pipe(tap((modal) => modal.createEmptyRecord()));
    }
    dynamicEditorPropertiesGet(record, columnId, editorProps) {
        return editorProps;
    }
    contactSelectModalGet() {
        return DIContainer.resolveLazy('ContactSelectModal')
            .pipe(tap((modal) => {
            modal.createEmptyRecord();
        }));
    }
    siteSelectModalGet() {
        return DIContainer.resolveLazy('SiteSelectModal')
            .pipe(tap((modal) => {
            modal.createEmptyRecord();
        }));
    }
    masterSelectModalGet() {
        return DIContainer.resolveLazy('MasterSelectModal')
            .pipe(tap((modal) => {
            modal.createEmptyRecord();
        }));
    }
    passwordConfirmModalGet() {
        return DIContainer.resolveLazy('PasswordConfirmModal');
    }
    passwordModalGet() {
        return DIContainer.resolveLazy('PasswordModal');
    }
    printModalGet() {
        return DIContainer.resolveLazy('PrintModal');
    }
    projectSelectModalGet() {
        return DIContainer.resolveLazy('ProjectSelectModal')
            .pipe(tap((modal) => {
            modal.createEmptyRecord();
        }));
    }
    GlobalScheduleModalGet() {
        return DIContainer.resolveLazy('GlobalScheduleModal')
            .pipe(tap((modal) => {
            modal.createEmptyRecord();
        }));
    }
    signatureModalGet(modalClassName = 'SignatureModal') {
        return DIContainer.resolveLazy(modalClassName)
            .pipe(tap((modal) => {
            modal.createEmptyRecord();
        }));
    }
    zipTaxModalGet(zip) {
        const modal = DIContainer.new('Modal');
        modal.captionKey = 'modal_ziptax';
        modal.createEmptyRecord();
        modal.record.set('zip', zip);
        const zipFilter = DIContainer.new('Input', 'zip');
        zipFilter.captionKey = 'modal_ziptax_zip';
        const grid = DIContainer.new('Grid');
        grid.includeSelectButton = true;
        grid.dbGridName = 'ziptax';
        grid.endpoint = 'ziptax';
        grid.action = 'list';
        grid.dynamicParameters = {
            zip: 'zip'
        };
        modal.componentAdd(zipFilter);
        modal.componentAdd(grid);
        // keep consistent with observable nature of other modal functions in here
        return of(modal);
    }
    /**
     * Called after a value is changed by a user interaction (ie. an input or editing a grid cell)
     * @param _context
     * @param _column
     * @param _value
     * @param _oldvalue
     */
    userChange(_context, _column, _value, _oldvalue) {
    }
    specialEditColumnsGet(record) {
        if (record.get('_specialeditcolumns')) {
            return record.get('_specialeditcolumns').split(',');
        }
        else {
            return this.defaultSpecialEditColumns;
        }
    }
    // if verbose is true, you can optionally display an error message as to something is not editable
    // ex. When user is clicking to edit a grid cell
    isEditable(record, columnName, verbose = false) {
        if (!record.permEdit) {
            return false;
        }
        let conditionPass = true;
        if (Reflect.has(record.data, '_editcondition')) {
            conditionPass = this.ConditionsManager.validateConditions(record.get('_editcondition'));
        }
        if (conditionPass && columnName && Reflect.has(record.data, `_editcondition_${columnName.toLowerCase()}`)) {
            conditionPass = this.ConditionsManager.validateConditions(record.get(`_editcondition_${columnName.toLowerCase()}`));
        }
        if (conditionPass && columnName && record.get('_specialeditmode') === 'Y') {
            conditionPass = this.specialEditColumnsGet(record).indexOf(columnName) > -1;
        }
        return conditionPass;
    }
    // if verbose is true, you can optionally display an error message as to something is not deletable
    // ex. When user is attempting to delete a record
    isDeletable(record, verbose = false) {
        let conditionPass = true;
        if (Reflect.has(record.data, '_deletecondition')) {
            conditionPass = this.ConditionsManager.validateConditions(record.get('_deletecondition'));
        }
        return conditionPass;
    }
    /**
     * Called after a value is changed by any source.
     * @param _record
     * @param _column
     * @param _value
     * @param _oldvalue
     * @param _source
     */
    programmaticChange(_record, _column, _value, _oldvalue, _source) {
    }
    openRecord(primaryKey) {
        zoomHandler(`/form/${this.formName}?${this.pkName}={{primarykey}}`, {
            target: '',
            data: {
                primarykey: primaryKey
            }
        });
    }
    previewCreateRecord(context, parameters, additionalRecordData = {}) {
        parameters = { ...parameters, ...{ _preview: 'Y' } };
        return this.createRecord(context, parameters, additionalRecordData);
    }
    /**
     * This should be called for any record creation whether from a form or grid add button.
     * @param context
     * @param parameters
     * @param additionalRecordData
     */
    createRecord(context, parameters, additionalRecordData = {}) {
        if (!isArray(additionalRecordData)) {
            additionalRecordData = [additionalRecordData];
        }
        if (this.autoMergeParamsOnCreate && parameters) {
            additionalRecordData = additionalRecordData || [];
            if (!additionalRecordData.length) {
                additionalRecordData.push({});
            }
            additionalRecordData.forEach((data) => {
                for (const key in parameters) {
                    if (['_tablename', '_outputdata'].indexOf(key) === -1) {
                        data[key] = parameters[key] || '';
                    }
                }
            });
        }
        return this.createRecordDefaultValues(context, parameters || {}, additionalRecordData).pipe(switchMap(({ parameters: enrichedParams, data: enrichedData }) => {
            additionalRecordData = enrichedData;
            parameters = enrichedParams;
            return DIContainer.getInstance('Request').PUT({
                endpoint: 'Record',
                action: '',
                initiator: context.initiator,
                stream: additionalRecordData.length > 10,
                requestData: {
                    jsonRequest: JSON.stringify({ griddata: { record: additionalRecordData } }),
                    ...{ ...{ _tablename: this.tableName, _outputdata: 'Y', _requestreverttoken: 'Y' }, ...parameters }
                }
            });
        }), tap((response) => {
            if (parameters?._preview !== 'Y') {
                const tableName = parameters?._tablename || this.tableName;
                const newRecordData = response.payload[tableName] || [];
                const payload = newRecordData.map((data) => {
                    return {
                        data,
                        loadedData: data
                    };
                });
                if (payload.length) {
                    DIContainer.getInstance('BroadcastManager').send(`recordcreate_${tableName}`, {
                        payload
                    });
                }
            }
        }));
    }
    createRecordDefaultValues(_context, _parameters, data) {
        return of({ parameters: _parameters, data });
    }
    offlineRecordCreate(context, parameters, additionalRecordData = {}) {
        const tableName = (parameters?._tablename || this.tableName).toLowerCase();
        const pkColumnName = this.pkName || `${tableName}no`;
        const outboxManager = DIContainer.getInstance('OfflineOutboxManager');
        if (!isArray(additionalRecordData)) {
            additionalRecordData = [additionalRecordData];
        }
        const mergedParams = { ...parameters, _offline: 'Y' };
        return this.createRecordDefaultValues(context, mergedParams, additionalRecordData).pipe(switchMap(({ parameters: enrichedParams, data: enrichedData }) => {
            const status = enrichedParams._outboxStatus || undefined;
            const creates = enrichedData.map((record) => outboxManager.createRecord$({
                _tablename: tableName,
                pkColumnName,
                ...(record._parentid ? { _parentid: String(record._parentid) } : {}),
                ...(status ? { status } : {}),
                data: record
            }));
            return forkJoin(creates);
        }));
    }
    /**
     * This is called for grid-level record saves or extended menu updates etc.
     * This cannot be called for Form-level saves because they are handling many different types of records.
     * @param context
     * @param tablename
     * @param records
     * @param deletedData
     * @param tablename
     */
    save(context, records, deletedData, tablename) {
        let tableName = tablename || this.tableName;
        if (!this.tableName && records.length) {
            tableName = records[0].tableName;
        }
        let data = records.map((record) => record.requestPackageGet());
        if (deletedData && deletedData.length) {
            data = data.concat(deletedData.map((record) => record.requestPackageGet()));
        }
        return DIContainer.getInstance('Request').PATCH({
            endpoint: 'Record',
            action: '',
            initiator: context.initiator,
            requestData: {
                jsonRequest: JSON.stringify({ griddata: { record: data } }),
                _tablename: tableName
            }
        });
    }
    /**
     * This should be called for any record deletion whether from a form or grid delete button.
     * @param context
     * @param records
     * @param options
     */
    delete(context, records, options) {
        // Check _deletecondition for each record
        for (const record of records) {
            if (!this.isDeletable(record, true)) {
                throw new Error('Selection does not allow delete');
            }
        }
        // options.previewDelete for situations like a grid deleting while in edit mode. We dont need to actually delete from the db at that point, so its just a hook for calculations like re-totaling a parent row or doing async validations.
        if (options && options.previewDelete && !this.validateOnPreviewDelete) {
            records.forEach((record) => {
                record.deleteFlag = 'Y';
            });
            return of(true);
        }
        let tableName = this.tableName;
        if (!this.tableName && records.length) {
            tableName = records[0].tableName;
        }
        return DIContainer.getInstance('Request').DELETE({
            endpoint: 'Record',
            action: '',
            initiator: context.initiator,
            requestData: {
                jsonRequest: JSON.stringify({ griddata: {
                        record: records.map((record) => {
                            // record.deleteFlag = 'Y';
                            return { ...record.requestPackageGet(), ...{ _deleteflag: 'Y' } };
                        })
                    }
                }),
                _tablename: tableName,
                validateonly: options?.previewDelete && this.validateOnPreviewDelete ? 'Y' : 'N'
            }
        }).pipe(tap(() => {
            if (!options?.previewDelete) {
                const payload = records.map((record) => {
                    return {
                        data: record.data,
                        loadedData: record.loadedData
                    };
                });
                if (payload.length) {
                    DIContainer.getInstance('BroadcastManager').send(`recorddelete_${tableName}`, {
                        payload
                    });
                }
            }
            records.forEach((record) => {
                record.deleteFlag = 'Y';
            });
        }));
    }
    /*
    * utility so alerts are easier to display from recordLogic subclasses
    * */
    alert(options) {
        DIContainer.getInstance('Application').alert(options);
    }
    prompt(options) {
        DIContainer.getInstance('Application').prompt(options);
    }
    handleRecordsAddedToGrid(_records) {
    }
}
/** The version of the RecordLogic class */
RecordLogic.VERSION = '$Revision: 116739 $';
